/**
 * RolesPermisosAdmin — qué puede hacer cada rol (C3D-27).
 *
 * Cada rol es un grupo de Django en el backend; aquí se marcan los permisos
 * de ese grupo que gestiona el catálogo (`users/roles.py`): qué
 * customizadores usa y si descarga ficheros de cualquier diseño. El rol admin
 * no se edita: el backend lo deja hacer todo.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, Lock, RotateCw, Save, ShieldCheck, Undo2 } from "lucide-react";
import { toast } from "react-toastify";
import { getThemeContext } from "../../../../components/providers/ThemeProvider.jsx";

const sameSet = (a = [], b = []) => a.length === b.length && a.every((item) => b.includes(item));

export default function RolesPermisosAdmin() {
    const theme = getThemeContext();
    const [state, setState] = useState({ status: "loading", catalog: [], roles: [] });
    // Lo que se ha marcado sin guardar, por rol: { manager: [...permisos] }.
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setState((prev) => ({ ...prev, status: "loading" }));
        try {
            const { data } = await axios.get("/role-permissions/");
            setState({ status: "ready", catalog: data.catalog, roles: data.roles });
            setDraft({});
        } catch {
            setState((prev) => ({ ...prev, status: "error" }));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const permissionsOf = useCallback(
        (role) => draft[role.role] ?? role.permissions,
        [draft],
    );

    const changedRoles = useMemo(
        () => state.roles.filter((role) => role.editable && draft[role.role]
            && !sameSet(draft[role.role], role.permissions)),
        [state.roles, draft],
    );

    const toggle = (role, permission) => {
        const current = permissionsOf(role);
        const next = current.includes(permission)
            ? current.filter((item) => item !== permission)
            : [...current, permission];
        setDraft((prev) => ({ ...prev, [role.role]: next }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const saved = await Promise.all(changedRoles.map((role) => axios
                .put(`/role-permissions/${role.role}/`, { permissions: draft[role.role] })
                .then(({ data }) => data)));
            setState((prev) => ({
                ...prev,
                roles: prev.roles.map((role) => saved.find((item) => item.role === role.role) ?? role),
            }));
            setDraft({});
            toast.success("Permisos guardados. Cada usuario los verá al recargar la página.", { theme: theme === "dark" ? "dark" : "light" });
        } catch (error) {
            toast.error(error.response?.data?.detail || "No se pudieron guardar los permisos.", { theme: theme === "dark" ? "dark" : "light" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 lg:p-6 flex flex-col gap-4 h-full overflow-auto">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <ShieldCheck className="w-7 h-7 text-primary shrink-0" />
                    <div>
                        <h1 className="text-xl font-bold">Roles y permisos</h1>
                        <p className="text-sm text-base-content/60">
                            Qué puede hacer cada rol. Se aplica a todos los usuarios con ese rol.
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm gap-1"
                        onClick={() => setDraft({})}
                        disabled={!changedRoles.length || saving}
                    >
                        <Undo2 className="w-4 h-4" /> Descartar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm gap-1"
                        onClick={save}
                        disabled={!changedRoles.length || saving}
                    >
                        {saving
                            ? <span className="loading loading-spinner loading-xs" />
                            : <Save className="w-4 h-4" />}
                        Guardar cambios
                    </button>
                </div>
            </header>

            {state.status === "loading" && (
                <div className="flex justify-center py-10">
                    <span className="loading loading-spinner loading-lg text-primary" />
                </div>
            )}

            {state.status === "error" && (
                <div role="alert" className="alert alert-error text-sm flex-wrap">
                    <AlertTriangle className="w-4 h-4" />
                    <span>No se pudieron cargar los roles.</span>
                    <button type="button" onClick={load} className="btn btn-xs gap-1">
                        <RotateCw className="w-3 h-3" /> Reintentar
                    </button>
                </div>
            )}

            {state.status === "ready" && state.catalog.map((section) => (
                <section key={section.section} className="card bg-base-100 border border-base-300">
                    <div className="card-body p-4 gap-3">
                        <div>
                            <h2 className="font-semibold">{section.section}</h2>
                            <p className="text-sm text-base-content/60">{section.description}</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th className="min-w-[12rem]">Permiso</th>
                                        {state.roles.map((role) => (
                                            <th key={role.role} className="text-center">
                                                <div className="flex flex-col items-center">
                                                    <span>{role.label}</span>
                                                    <span className="text-xs font-normal text-base-content/50">
                                                        {role.user_count} {role.user_count === 1 ? "usuario" : "usuarios"}
                                                    </span>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {section.items.map((item) => (
                                        <tr key={item.permission}>
                                            <td>{item.label}</td>
                                            {state.roles.map((role) => (
                                                <td key={role.role} className="text-center">
                                                    {role.editable ? (
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox checkbox-sm checkbox-primary"
                                                            checked={permissionsOf(role).includes(item.permission)}
                                                            onChange={() => toggle(role, item.permission)}
                                                            aria-label={`${item.label} para ${role.label}`}
                                                            disabled={saving}
                                                        />
                                                    ) : (
                                                        <span
                                                            className="inline-flex text-base-content/40"
                                                            title="Administración lo puede todo"
                                                        >
                                                            <Lock className="w-4 h-4" aria-label="Siempre" />
                                                        </span>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            ))}

            {state.status === "ready" && (
                <p className="text-xs text-base-content/50">
                    Quitar un customizador no borra nada: quien ya tenga diseños hechos con él los
                    sigue viendo, pero no puede crear ni guardar. Quien compra un diseño siempre puede
                    descargarlo.
                </p>
            )}
        </div>
    );
}
